/**
 * Responsibility: Implement standard actions behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
// ============================================================
// STANDARD-ACTIONS.TS — Tier-0 deterministic action handlers
//
// Common verbs (TAKE, DROP, EQUIP, UNEQUIP, EXAMINE, WAIT) need
// no AI judgment. Handled synchronously before Logic Forge runs.
// Narrator still runs for all actions — only state mutation skips AI.
// ============================================================

import type { GameState, LogicUpdate, LogicForgeMeta } from "@anyquest/shared-core"
import { ActionCategory } from "@anyquest/shared-core"
export interface StandardActionResult {
  logicUpdate: LogicUpdate
  meta: LogicForgeMeta
  logicFnString: string
}

// Three-tier fuzzy match: exact → query contains item → item contains query
function matchItem(items: string[], query: string): string | null {
  const q = query.toLowerCase().trim()
  return (
    items.find(i => i.toLowerCase() === q) ??
    items.find(i => q.includes(i.toLowerCase())) ??
    items.find(i => i.toLowerCase().includes(q)) ??
    null
  )
}

function makeResult(
  category: ActionCategory,
  successLevel: number,
  updates: LogicUpdate['updates'],
): StandardActionResult {
  return {
    logicUpdate: { successLevel, updates },
    meta: { cacheable: true, category },
    logicFnString: `(state, roll) => (${JSON.stringify({ successLevel, updates })})`,
  }
}

export function tryStandardAction(gs: GameState, input: string): StandardActionResult | null {
  const trimmed = input.trim()
  // TAKE / PICK UP
  const takeMatch = trimmed.match(/^(?:take|pick up)\s+(.+)$/i)
  if (takeMatch) {
    const item = matchItem(gs.currentRoom.items, takeMatch[1])
    if (item) {
      return makeResult(ActionCategory.take, 2, {
        timeCost: 2,
        inventoryAdd: [item],
        roomItemsRemove: [item],
      })
    }
    // Item not in room — fall through to Logic Forge for richer failure narration
    return null
  }

  // DROP
  const dropMatch = trimmed.match(/^drop\s+(.+)$/i)
  if (dropMatch) {
    const invNames = gs.player.inventory.filter(i => !i.equipped).map(i => i.name)
    const item = matchItem(invNames, dropMatch[1])
    if (item) {
      return makeResult(ActionCategory.use, 2, {
        timeCost: 2,
        inventoryRemove: [item],
        roomItemsAdd: [item],
      })
    }
    return null
  }

  // EQUIP
  const equipMatch = trimmed.match(/^equip\s+(.+)$/i)
  if (equipMatch) {
    const unequipped = gs.player.inventory.filter(i => !i.equipped).map(i => i.name)
    const item = matchItem(unequipped, equipMatch[1])
    if (item) {
      return makeResult(ActionCategory.use, 2, {
        timeCost: 1,
        inventoryEquip: [item],
      })
    }
    return null
  }

  // UNEQUIP
  const unequipMatch = trimmed.match(/^unequip\s+(.+)$/i)
  if (unequipMatch) {
    const equipped = gs.player.inventory.filter(i => i.equipped).map(i => i.name)
    const item = matchItem(equipped, unequipMatch[1])
    if (item) {
      return makeResult(ActionCategory.use, 2, {
        timeCost: 1,
        inventoryUnequip: [item],
      })
    }
    return null
  }

  // EXAMINE / LOOK AT / INSPECT / STUDY
  if (/^(?:examine|look at|inspect|study)\s+.+$/i.test(trimmed)) {
    return makeResult(ActionCategory.examine, 1, { timeCost: 2 })
  }

  // WAIT / REST / STAY / PAUSE
  if (/^(?:wait|rest|stay|pause)(?:\s|$)/i.test(trimmed)) {
    return makeResult(ActionCategory.other, 1, { timeCost: 15 })
  }

  return null
}
